<template>
  <!-- Modal backdrop -->
  <div
    v-if="visible"
    class="modal-backdrop fade show"
    @click="$emit('close')"
  ></div>

  <!-- Modal dialog -->
  <div
    v-if="visible"
    class="modal fade show d-block"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @keydown.esc="$emit('close')"
  >
    <div class="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable" role="document">
      <div class="modal-content" @click.stop>
        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title">Generate eBay Listing</h5>
          <button type="button" class="btn-close" aria-label="Close" @click="$emit('close')"></button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <!-- Title preview -->
          <div class="mb-3">
            <label class="form-label fw-semibold">Listing Title</label>
            <div class="d-flex gap-2 align-items-start">
              <input
                v-model="listingTitle"
                type="text"
                class="form-control form-control-sm"
                maxlength="80"
              />
              <span class="badge" :class="listingTitle.length > 80 ? 'bg-danger' : 'bg-secondary'">
                {{ listingTitle.length }}/80
              </span>
            </div>
          </div>

          <!-- HTML preview -->
          <label class="form-label fw-semibold">Description Preview</label>
          <div class="border rounded p-3 bg-white mb-3" style="max-height: 500px; overflow-y: auto;">
            <div v-html="listingHtml"></div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" @click="$emit('close')">
            Close
          </button>
          <button type="button" class="btn btn-outline-primary" @click="copyTitle">
            {{ titleCopied ? 'Title Copied!' : 'Copy Title' }}
          </button>
          <button type="button" class="btn btn-primary" @click="copyHtml">
            {{ htmlCopied ? 'HTML Copied!' : 'Copy HTML' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "EbayListingModal",
  props: {
    visible: { type: Boolean, default: false },
    order: { type: Object, default: null },
  },
  emits: ["close"],
  data() {
    return {
      listingTitle: "",
      listingHtml: "",
      htmlCopied: false,
      titleCopied: false,
    };
  },
  watch: {
    visible(val) {
      if (val && this.order) {
        this.generate();
      }
    },
  },
  methods: {
    generate() {
      const dm = this.order.device_model_details;
      const o = this.order;

      // Resolve fields — prefer device_model_details, fall back to order fields
      const make = dm?.make || o.device_make || "";
      const modelName = dm?.model_name || o.device_model || "";
      const commonName = dm?.common_name || o.device_commonname || "";
      const cpu = dm?.cpu || "";
      const gpu = dm?.gpu || "";
      const ram = dm?.ram || "";
      const storage = dm?.storage || "";
      const screenSize = dm?.screen_size || "";
      const ports = dm?.ports || "";
      const features = dm?.features || "";
      const whatsIncluded = dm?.whats_included || "";
      const listingNotes = dm?.listing_notes || "";
      const templateHtml = dm?.listing_template_html || "";
      const displayResolution = dm?.display_resolution || "";
      const connectivity = dm?.connectivity || "";
      const batteryInfo = dm?.battery_info || "";
      const weight = dm?.weight || "";

      // Per-unit fields from order
      const installedOs = o.installed_os || "";
      const cosmeticGrade = o.cosmetic_grade || "";
      const cosmeticNotes = o.cosmetic_notes || "";

      // Generate title (80 char eBay limit)
      this.listingTitle = this.buildTitle(make, modelName, screenSize, cpu, gpu, ram, storage);

      // If custom template HTML exists, do placeholder substitution
      if (templateHtml.trim()) {
        this.listingHtml = templateHtml
          .replace(/\{\{cpu\}\}/g, cpu)
          .replace(/\{\{gpu\}\}/g, gpu)
          .replace(/\{\{ram\}\}/g, ram)
          .replace(/\{\{storage\}\}/g, storage)
          .replace(/\{\{screen_size\}\}/g, screenSize)
          .replace(/\{\{make\}\}/g, make)
          .replace(/\{\{model_name\}\}/g, modelName)
          .replace(/\{\{common_name\}\}/g, commonName)
          .replace(/\{\{ports\}\}/g, ports)
          .replace(/\{\{features\}\}/g, features)
          .replace(/\{\{whats_included\}\}/g, whatsIncluded)
          .replace(/\{\{listing_notes\}\}/g, listingNotes)
          .replace(/\{\{installed_os\}\}/g, installedOs)
          .replace(/\{\{cosmetic_grade\}\}/g, cosmeticGrade)
          .replace(/\{\{cosmetic_notes\}\}/g, cosmeticNotes)
          .replace(/\{\{display_resolution\}\}/g, displayResolution)
          .replace(/\{\{connectivity\}\}/g, connectivity)
          .replace(/\{\{battery_info\}\}/g, batteryInfo)
          .replace(/\{\{weight\}\}/g, weight);
        return;
      }

      // Device type from catalog or order
      const deviceType = dm?.device_type || o.device_type || "Laptop";

      // Auto-generate HTML listing
      this.listingHtml = this.buildHtml({
        make, modelName, commonName, cpu, gpu, ram, storage, screenSize,
        ports, features, whatsIncluded, listingNotes,
        displayResolution, connectivity, batteryInfo, weight,
        installedOs, cosmeticGrade, cosmeticNotes, deviceType,
      });
    },

    buildTitle(make, model, screen, cpu, gpu, ram, storage) {
      // Shorten CPU: "Intel Core i7-10850H" -> "i7-10850H"
      const cpuShort = cpu.replace(/Intel\s+Core\s+/i, "").replace(/AMD\s+Ryzen\s+/i, "Ryzen ").split(" ")[0] || "";
      // Shorten GPU: "NVIDIA Quadro T1000 (4GB GDDR6)" -> "Quadro T1000"
      const gpuShort = gpu.replace(/NVIDIA\s+/i, "").replace(/\s*\(.*?\)/g, "").trim();
      const screenPart = screen ? `${screen}` : "";

      // Build: Make Model Screen CPU GPU RAM Storage - Refurbished
      const parts = [make, model, screenPart, cpuShort, gpuShort, ram, storage].filter(Boolean);
      let title = parts.join(" ").replace(/\s+/g, " ").trim();

      // Append "- Refurbished" for eBay SEO
      const suffix = " - Refurbished";

      if ((title + suffix).length <= 80) {
        return title + suffix;
      }

      // Try removing GPU first
      const partsNoGpu = [make, model, screenPart, cpuShort, ram, storage].filter(Boolean);
      title = partsNoGpu.join(" ").replace(/\s+/g, " ").trim();
      if ((title + suffix).length <= 80) {
        return title + suffix;
      }

      // Try removing screen
      const partsNoScreen = [make, model, cpuShort, ram, storage].filter(Boolean);
      title = partsNoScreen.join(" ").replace(/\s+/g, " ").trim();
      if ((title + suffix).length <= 80) {
        return title + suffix;
      }

      // Hard truncate
      const maxBase = 80 - suffix.length;
      return title.substring(0, maxBase).trim() + suffix;
    },

    splitToBullets(text) {
      if (!text) return [];
      return text.split(/[\n,]+/).map(s => s.trim()).filter(Boolean);
    },

    buildHtml(d) {
      const deviceName = d.commonName || `${d.make} ${d.modelName}`;
      const isLaptop = (d.deviceType || "Laptop") === "Laptop";
      const isDesktop = (d.deviceType || "Laptop") === "Desktop";
      const deviceLabel = isDesktop ? "Desktop" : isLaptop ? "Laptop" : "Device";
      const subtitle = [d.cpu, d.gpu, d.ram, d.storage].filter(Boolean).join(" | ");

      // Device-type-aware defaults
      const defaultIncluded = isDesktop
        ? "Desktop, AC power cable"
        : "Laptop, AC power adapter";
      const defaultNotes = "Photos are representative of the model and configuration. This unit has been professionally refurbished and tested. All devices are cleaned, inspected, and updated before shipping.";

      const gradeColors = {
        Excellent: { bg: "#d4edda", text: "#155724", border: "#c3e6cb" },
        Good: { bg: "#d1ecf1", text: "#0c5460", border: "#bee5eb" },
        Fair: { bg: "#fff3cd", text: "#856404", border: "#ffeeba" },
        Acceptable: { bg: "#ffeeba", text: "#856404", border: "#ffd96b" },
      };

      let html = "";

      // ── 1. Branded Header ──
      html += `
<div style="font-family: Arial, Helvetica, sans-serif; max-width: 800px; margin: 0 auto; color: #333;">
  <div style="background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); color: white; padding: 30px; border-radius: 12px 12px 0 0; text-align: center;">
    <h1 style="margin: 0 0 8px 0; font-size: 26px; font-weight: 700;">${this.esc(deviceName)}</h1>
    <p style="margin: 0; font-size: 14px; opacity: 0.85;">${this.esc(subtitle)}</p>
    <p style="margin: 10px 0 0 0; font-size: 13px; opacity: 0.7;">Professionally Refurbished &amp; Tested by Second Wind</p>
  </div>`;

      // ── 2. Condition at a Glance (badges) ──
      if (d.cosmeticGrade) {
        const gc = gradeColors[d.cosmeticGrade] || gradeColors.Good;
        html += `
  <div style="background: #fff; padding: 20px 24px; border-left: 1px solid #dee2e6; border-right: 1px solid #dee2e6; display: flex; justify-content: center; gap: 16px; flex-wrap: wrap;">
    <div style="background: ${gc.bg}; color: ${gc.text}; border: 1px solid ${gc.border}; padding: 8px 20px; border-radius: 8px; font-weight: 600; font-size: 14px; text-align: center;">
      &#9733; ${this.esc(d.cosmeticGrade)} Condition
    </div>
    <div style="background: #d1ecf1; color: #0c5460; border: 1px solid #bee5eb; padding: 8px 20px; border-radius: 8px; font-weight: 600; font-size: 14px; text-align: center;">
      &#10003; Professionally Tested
    </div>
    <div style="background: #e2e3f1; color: #383d6e; border: 1px solid #c8cbe9; padding: 8px 20px; border-radius: 8px; font-weight: 600; font-size: 14px; text-align: center;">
      &#128274; eBay Buyer Protection
    </div>
  </div>`;
      }

      // ── 3. Installed Configuration ──
      // Build specs list — device-type aware ordering and labels
      const specs = [];
      if (d.cpu) specs.push({ label: "Processor", value: d.cpu });
      if (d.gpu) specs.push({ label: "Graphics", value: d.gpu });
      if (d.ram) specs.push({ label: "Memory", value: d.ram });
      if (d.storage) specs.push({ label: "Storage", value: d.storage });

      // Display — combine screen + resolution, label changes for desktop
      const displayVal = [d.screenSize, d.displayResolution].filter(Boolean).join(" &mdash; ");
      if (displayVal) {
        specs.push({
          label: isDesktop ? "Display Output" : "Display",
          value: displayVal,
        });
      }

      if (d.installedOs) specs.push({ label: "Operating System", value: d.installedOs });
      if (d.connectivity) specs.push({ label: "Connectivity", value: d.connectivity });

      // Battery for laptops, Power/Form Factor for desktops
      if (d.batteryInfo) {
        specs.push({
          label: isDesktop ? "Power / Form Factor" : "Battery",
          value: d.batteryInfo,
        });
      }

      if (d.weight) {
        specs.push({
          label: isDesktop ? "Weight / Dimensions" : "Weight",
          value: d.weight,
        });
      }

      if (specs.length) {
        html += `
  <div style="background: #f8f9fa; padding: 24px; border-left: 1px solid #dee2e6; border-right: 1px solid #dee2e6;">
    <h2 style="font-size: 18px; color: #1a1a2e; margin: 0 0 16px 0; border-bottom: 2px solid #1a1a2e; padding-bottom: 8px;">Installed Configuration</h2>
    <table style="width: 100%; border-collapse: collapse;">`;
        for (const s of specs) {
          html += `
      <tr>
        <td style="padding: 8px 12px; font-weight: 600; width: 160px; vertical-align: top; color: #555;">${this.esc(s.label)}</td>
        <td style="padding: 8px 12px;">${s.value}</td>
      </tr>`;
        }
        html += `
    </table>
  </div>`;
      }

      // ── 4. Key Features (bullet list) ──
      const featureBullets = this.splitToBullets(d.features);
      if (featureBullets.length) {
        html += `
  <div style="background: #fff; padding: 24px; border-left: 1px solid #dee2e6; border-right: 1px solid #dee2e6;">
    <h2 style="font-size: 18px; color: #1a1a2e; margin: 0 0 16px 0; border-bottom: 2px solid #1a1a2e; padding-bottom: 8px;">Key Features</h2>
    <ul style="margin: 0; padding-left: 0; list-style: none;">`;
        for (const f of featureBullets) {
          html += `
      <li style="padding: 4px 0; font-size: 14px;">&#10004; ${this.esc(f)}</li>`;
        }
        html += `
    </ul>
  </div>`;
      }

      // ── 5. Ports & Connectivity (bullet list, with header labels for desktop) ──
      const portBullets = this.splitToBullets(d.ports);
      if (portBullets.length) {
        html += `
  <div style="background: #f8f9fa; padding: 24px; border-left: 1px solid #dee2e6; border-right: 1px solid #dee2e6;">
    <h2 style="font-size: 18px; color: #1a1a2e; margin: 0 0 16px 0; border-bottom: 2px solid #1a1a2e; padding-bottom: 8px;">Ports &amp; Connectivity</h2>
    <ul style="margin: 0; padding-left: 0; list-style: none;">`;
        for (const p of portBullets) {
          // Detect section headers like "Front:" or "Rear:" for desktops
          const isHeader = /^(front|rear|left|right|back|top|bottom|side):?\s*$/i.test(p.trim());
          if (isHeader) {
            html += `
      <li style="padding: 10px 0 4px 0; font-size: 14px; font-weight: 700; color: #1a1a2e;">${this.esc(p.replace(/:?\s*$/, ''))}</li>`;
          } else {
            html += `
      <li style="padding: 4px 0; font-size: 14px;">&#10004; ${this.esc(p)}</li>`;
          }
        }
        html += `
    </ul>
  </div>`;
      }

      // ── 6. Condition Details (only if cosmetic_notes) ──
      if (d.cosmeticNotes) {
        html += `
  <div style="background: #fff8e1; padding: 24px; border-left: 1px solid #dee2e6; border-right: 1px solid #dee2e6; border-top: 1px solid #ffeeba;">
    <h2 style="font-size: 18px; color: #856404; margin: 0 0 12px 0; border-bottom: 2px solid #856404; padding-bottom: 8px;">Condition Details</h2>
    <p style="margin: 0; line-height: 1.7; color: #664d03;">
      <strong>Grade: ${this.esc(d.cosmeticGrade || "N/A")}</strong><br/>
      ${this.esc(d.cosmeticNotes)}
    </p>
  </div>`;
      }

      // ── 7. What's Included ──
      const includedItems = this.splitToBullets(d.whatsIncluded || defaultIncluded);
      html += `
  <div style="background: ${d.cosmeticNotes ? '#f8f9fa' : '#fff'}; padding: 24px; border-left: 1px solid #dee2e6; border-right: 1px solid #dee2e6;">
    <h2 style="font-size: 18px; color: #1a1a2e; margin: 0 0 16px 0; border-bottom: 2px solid #1a1a2e; padding-bottom: 8px;">What's Included</h2>
    <ul style="margin: 0; padding-left: 0; list-style: none;">`;
      for (const item of includedItems) {
        html += `
      <li style="padding: 4px 0; font-size: 14px;">&#9745; ${this.esc(item)}</li>`;
      }
      html += `
    </ul>
  </div>`;

      // ── 8. About Second Wind Refurbish ──
      html += `
  <div style="background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%); color: white; padding: 28px; border-left: 1px solid #1a1a2e; border-right: 1px solid #1a1a2e;">
    <h2 style="font-size: 18px; margin: 0 0 12px 0; border-bottom: 2px solid rgba(255,255,255,0.3); padding-bottom: 8px; color: white;">Why Buy From Second Wind?</h2>
    <p style="margin: 0 0 12px 0; line-height: 1.7; font-size: 14px; opacity: 0.92;">
      Every device undergoes our comprehensive refurbishment process: thorough inspection, professional cleaning, hardware testing, OS installation, and quality assurance. We're committed to giving great technology a second life.
    </p>
    <p style="margin: 0; font-size: 14px; font-style: italic; opacity: 0.8;">
      Second Wind Refurbish &mdash; Quality You Can Trust, Value You Can Afford.
    </p>
  </div>`;

      // ── 9. Fine Print / Notes ──
      const notes = d.listingNotes || defaultNotes;
      html += `
  <div style="background: #f8f9fa; padding: 20px 24px; border: 1px solid #dee2e6; border-radius: 0 0 12px 12px;">
    <p style="margin: 0; line-height: 1.6; color: #888; font-size: 12px;">
      ${this.esc(notes)}
      ${!d.listingNotes ? ' All purchases are covered by eBay Buyer Protection.' : ''}
    </p>
  </div>
</div>`;

      return html;
    },

    esc(str) {
      const div = document.createElement("div");
      div.textContent = str;
      return div.innerHTML;
    },

    async copyHtml() {
      try {
        await navigator.clipboard.writeText(this.listingHtml);
        this.htmlCopied = true;
        setTimeout(() => { this.htmlCopied = false; }, 2000);
      } catch {
        this.fallbackCopy(this.listingHtml);
        this.htmlCopied = true;
        setTimeout(() => { this.htmlCopied = false; }, 2000);
      }
    },

    async copyTitle() {
      try {
        await navigator.clipboard.writeText(this.listingTitle);
        this.titleCopied = true;
        setTimeout(() => { this.titleCopied = false; }, 2000);
      } catch {
        this.fallbackCopy(this.listingTitle);
        this.titleCopied = true;
        setTimeout(() => { this.titleCopied = false; }, 2000);
      }
    },

    fallbackCopy(text) {
      const ta = document.createElement("textarea");
      ta.value = text;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    },
  },
};
</script>

<style scoped>
.modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.modal {
  z-index: 1050;
}
</style>
